import { Injectable } from '@angular/core';
import { Diagnostic, DiagnosticApi } from '../model/diagnostic.model';
import { HttpService } from 'src/app/core/http/http.service';
import { deepCloning } from 'src/app/shared/utils/functions';
import { isSameDay } from 'src/app/shared/utils/dates';
import { BehaviorSubject } from 'rxjs';
import _ from 'lodash';

@Injectable({
  providedIn: 'root'
})
export class DiagnosticService {

  private diagnostics$ = new BehaviorSubject<Diagnostic[]>([]);
  private originalDiagnostics: Diagnostic[] = [];

  constructor(
    private httpService: HttpService) {
  }

  async getPatientDiagnostics(patientId: string) {
    const response = await this.httpService.req<Diagnostic[]>('getDiagnosis', 'patient', {
      apiVersion: 2,
      loaderKey: `get-diagnóstico`,
      params: {
        patientId,
        lg: 'pt-BR'
      },
      method: 'get'
    });

    if (response.status === 'ok') {
      if (response.result && response.result.length > 0) {
        const diagnostics = response.result.map(item => {
          if (item.creationDate) {
            isSameDay(new Date(item.creationDate), new Date('1900-01-01')) ? item.creationDate = null : '';
          }
          return item;
        });
        this.originalDiagnostics = deepCloning(diagnostics);
        this.setAllDiagnostics(diagnostics);
        return;
      } else {
        return;
      }
    } else {
      throw new Error(response.msg);
    }
  }

  getCids(filter: string, groupComorbity: string, groupOncological: string) {

    return this.httpService.req<any>('getCid', 'patient', {
      apiVersion: 2,
      params: {
        filter,
        groupComorbity,
        groupOncological,
        lg: 'pt-BR'
      },
      method: 'get'
    });
  }

  async getIcdo(filter: string) {

    const res = await this.httpService.req<any[]>('getIcd_o', 'patient', {
      apiVersion: 2,
      params: {
        filter,
        lg: 'pt-BR'
      },
      method: 'get'
    });
    if (res.status == 'notok') {
      throw new Error();
    }
    return res.result;
  }

  async getDeathLocations() {
    const res = await this.httpService.req<{ place: string, KEY: string }[]>('placeOfDeath', 'patient', {
      apiVersion: 2,
      params: {
        lg: 'pt-BR'
      },
      method: 'get'
    });
    if (res.status == 'notok') {
      throw new Error();
    }
    return res.result;
  }

  async postNewDeathLocation(place: string) {
    const response = await this.httpService.req('placeOfDeath', 'patient', {
      method: 'post',
      apiVersion: 2,
      params: {
        lg: 'pt-BR'
      },
      body: { place: place }
    });

    if (response.status === 'notok') {
      throw new Error();
    }
    return response.result;
  }

  async saveAllDiagnosticChanges(patientId: string, practitionerId: string) {

    const diagnostics = this.diagnostics$.value;

    if (!diagnostics || diagnostics.length === 0) {
      return;
    }

    const updatedDiagnostics = await Promise.all(diagnostics.map(async diagnostic => {
      if (!diagnostic) {
        return diagnostic;
      }

      const original = this.originalDiagnostics.find(item => item.uniqueId === diagnostic.uniqueId);
      const hasChanged = !original || !_.isEqual(
        _.omit(original, 'cid.canStageCid'),
        _.omit(deepCloning(diagnostic), 'cid.canStageCid')
      );

      if (!hasChanged) {
        return diagnostic;
      }

      const diagApi = new DiagnosticApi(diagnostic, patientId, practitionerId);

      try {
        const retorno = diagnostic.conditionId === ''
          ? await this.postDiagnostic(diagApi)
          : await this.putDiagnostic(diagApi);

        if (retorno.status === 'ok') {
          return deepCloning(retorno.result);
        }
      } catch (error) {
        // mantém o diagnóstico local em caso de erro na chamada
      }

      return diagnostic;
    }));

    this.originalDiagnostics = deepCloning(updatedDiagnostics);
    this.setAllDiagnostics(updatedDiagnostics);
  }

  async postDiagnostic(diagApi: any) {
    try {
      const response = this.httpService.req('postDiagnosis', 'patient', {
        method: 'post',
        apiVersion: 2,
        loaderKey: 'post-diagnóstico',
        params: {
          lg: 'pt-BR'
        },
        body: diagApi
      });
      return response;
    }
    catch (error) {
      return error;
    }
  }

  async putDiagnostic(diagApi: DiagnosticApi) {
    try {
      const response = this.httpService.req('PutDiagnosis', 'patient', {
        method: 'put',
        loaderKey: 'put-diagnóstico',
        apiVersion: 2,
        params: {
          lg: 'pt-BR',
          diagnosisId: diagApi.informations?.conditionId ?? ''
        },
        body: diagApi
      });
      return response;
    }
    catch (error) {
      return error;
    }
  }

  async deleteDiagnostic(diagnosisId: string) {
    try {
      const response = this.httpService.req('deleteDiagnosis', 'patient', {
        method: 'delete',
        apiVersion: 2,
        params: {
          diagnosisId,
          lg: 'pt-BR'
        }
      });
      return response;
    }
    catch (error) {
      return error;
    }
  }

  getAllDiagnostics() {
    return this.diagnostics$.asObservable();
  }

  setAllDiagnostics(diagnostics: Diagnostic[]) {
    this.diagnostics$.next(diagnostics);
  }
}
